import type { UploadRule } from "../constants/upload";

/**
 * Client-side upload pre-validation for the `documents` domain.
 *
 * This is a UX nicety, not a security control — the backend stays authoritative
 * (SPEC Constraints). It blocks obviously-invalid files before the network call so
 * the user gets instant, actionable feedback.
 *
 * Checks are **extension-primary** (D4): a file whose extension is in the allowlist
 * passes even if its mimetype is unexpected (docx mimetypes vary across OS/browser);
 * the ambiguous case is left to the backend's mapped `415`.
 */

export type UploadRejectReason = "size" | "type" | "empty";

export type UploadValidationResult =
  | { ok: true }
  | { ok: false; reason: UploadRejectReason };

export type RejectedUploadFile = { file: File; reason: UploadRejectReason };

export type UploadPartition = {
  accepted: File[];
  rejected: RejectedUploadFile[];
};

/** Lowercase extension including the leading dot, or `""` when there is none. */
const getExtension = (filename: string): string => {
  const dot = filename.lastIndexOf(".");
  return dot === -1 ? "" : filename.slice(dot).toLowerCase();
};

/**
 * Validate a single file against a rule. Order: empty → type → size, so a 0-byte
 * file reads as "empty", a wrong-extension file as "type", and an oversized but
 * otherwise-valid file as "size".
 */
export const validateUploadFile = (
  file: File,
  rule: UploadRule,
): UploadValidationResult => {
  if (file.size === 0) {
    return { ok: false, reason: "empty" };
  }

  const ext = getExtension(file.name);
  const allowedExtensions = rule.allowed.map((type) => type.extension);
  if (!allowedExtensions.includes(ext)) {
    return { ok: false, reason: "type" };
  }

  if (file.size > rule.maxSizeBytes) {
    return { ok: false, reason: "size" };
  }

  return { ok: true };
};

/**
 * Partition a multi-file selection into accepted/rejected, validating each file
 * independently (SPEC AC 3). Order is preserved within each bucket.
 */
export const partitionUploadFiles = (
  files: readonly File[],
  rule: UploadRule,
): UploadPartition => {
  const accepted: File[] = [];
  const rejected: RejectedUploadFile[] = [];

  for (const file of files) {
    const result = validateUploadFile(file, rule);
    if (result.ok) {
      accepted.push(file);
    } else {
      rejected.push({ file, reason: result.reason });
    }
  }

  return { accepted, rejected };
};

/** `accept` attribute value derived from the rule, e.g. `.pdf,.docx,.png`. */
export const getAcceptAttribute = (rule: UploadRule): string =>
  rule.allowed.map((type) => type.extension).join(",");

/** Human-readable formats label derived from the rule, e.g. `PDF, DOCX, PNG`. */
export const getAllowedTypesLabel = (rule: UploadRule): string =>
  rule.allowed.map((type) => type.label).join(", ");

/** Max size expressed in whole MB, for copy interpolation. */
export const getMaxSizeMb = (rule: UploadRule): number =>
  Math.round(rule.maxSizeBytes / (1024 * 1024));

/** Human-readable size for a File (MB when ≥ 0.1 MB, else KB), for error copy. */
export const formatUploadedFileSize = (file: File): string => {
  const sizeInMB = file.size / (1024 * 1024);
  if (sizeInMB >= 0.1) return `${sizeInMB.toFixed(2)} MB`;
  return `${(file.size / 1024).toFixed(1)} KB`;
};
